// @ts-nocheck
import axios from 'axios';
import _ from 'lodash';
import { create } from 'zustand';
import { API_URL } from '../configs';
import { roundDecimals } from '../helper';
import { toastTypes } from '../enums';
import getRoot from './root';
import useAuthStore from './auth';

let confirmPaymentsRef = null;

const usePaymentsStore = create((set, get) => ({
  loading: false,
  availablePlans: [],
  selectedPlan: [],
  appliedPromoCode: {},
  stripeToken: {},
  email: '',
  promoCode: '',
  // Kept as an action rather than a get/set accessor pair: zustand's set()
  // spreads state into a new object, which would freeze an accessor into a
  // plain data property after the first update.
  setConfirmPaymentsRef: (el) => { confirmPaymentsRef = el; },

  reset: () => set({
    availablePlans: [], selectedPlan: [], appliedPromoCode: {},
    stripeToken: {}, email: '', promoCode: '',
  }),

  subtotal: () => roundDecimals(
    Object.values(get().selectedPlan).reduce(
      (acc, curr) => acc + (get().availablePlans.find((p) => p.id === curr) || { amount: 0 }).amount,
      0,
    ) / 100,
  ),
  promoValue: () => roundDecimals(get().subtotal() * get().appliedPromoCode.percentOff * 0.01 || 0),
  total: () => roundDecimals(get().subtotal() - get().promoValue()),

  getAvailablePlans: async () => {
    const root = getRoot();
    try {
      const res = await axios.get(`${API_URL}/api/payments/plans-list`);
      if (res.data.success) {
        set({ availablePlans: res.data.plans, email: useAuthStore.getState().user.email || '' });
      }
    } catch (e) {
      root.failure('Could not get available plans.');
    }
  },

  planSelector: (id) => set({ selectedPlan: [id] }),

  inputHandler: (_e, { name, value }) => set({ [name]: value }),

  verifyPromoCode: async () => {
    const root = getRoot();
    try {
      const promoVal = get().promoCode;
      const res = await axios.post(`${API_URL}/api/payments/check-promo`, { promoVal });
      if (res.data.success) {
        set({ appliedPromoCode: { promoVal, ...res.data } });
        root.success('The promo code has been applied.', toastTypes.verifyPromoCode);
        root.clearToast(toastTypes.verifyPromoCode);
      } else {
        root.failure('The promo code is invalid.', toastTypes.verifyPromoCode);
      }
    } catch (e) {
      root.failure('Could not verify promo code', toastTypes.verifyPromoCode);
    }
  },

  updateStripePayment: async () => {
    set({ loading: true });
    if (confirmPaymentsRef) confirmPaymentsRef.click();
    setTimeout(async () => {
      const root = getRoot();
      const { stripeToken } = get();
      if (Object.keys(stripeToken).length === 0) {
        set({ loading: false });
        return;
      }
      try {
        const res = await axios.patch(`${API_URL}/api/payments/account-details`, {
          updateObject: { source: stripeToken.id },
        });
        if (res.data.success) {
          root.success('Update Successful.', toastTypes.updateStripePayment);
          root.clearToast(toastTypes.updateStripePayment);
          root.accountStatus.getAccountDetails();
        } else {
          root.failure('Update Failed.', toastTypes.updateStripePayment);
        }
      } catch (e) {
        root.failure('Could not update your payment.', toastTypes.updateStripePayment);
      } finally {
        root.accountStatus.getAccountDetails();
        set({ loading: false });
      }
    }, 1500);
  },

  checkPayment: () => { if (confirmPaymentsRef) confirmPaymentsRef.click(); },

  confirmPaymentAndSubscribe: async () => {
    const root = getRoot();
    const { email, selectedPlan: selectedPlans, appliedPromoCode, stripeToken } = get();
    if (Object.keys(stripeToken).length === 0) return;
    if (selectedPlans.length === 0) {
      root.failure('Please select a plan.', toastTypes.confirmPaymentAndSubscribe);
      return;
    }
    try {
      set({ loading: true });
      const res = await axios.post(`${API_URL}/api/payments/subscription`, {
        email,
        promo: appliedPromoCode.promoVal,
        plan: Object.values(selectedPlans),
        stripeToken: stripeToken.id,
      });
      if (res.data.success) {
        root.success('Subscription Successful.', toastTypes.confirmPaymentAndSubscribe);
        root.clearToast(toastTypes.confirmPaymentAndSubscribe);
        root.accountStatus.getAccountDetails();
      } else {
        root.failure('Could not complete your subscription.', toastTypes.confirmPaymentAndSubscribe);
      }
    } catch (e) {
      root.failure('Could not complete your subscription.', toastTypes.confirmPaymentAndSubscribe);
    } finally {
      set({ loading: false });
    }
  },

  upgradePlan: async () => {
    const root = getRoot();
    set({ loading: true });
    const subscriptionId = _.get(root, ['accountStatus', 'account', 'subscriptions', 'data', '0', 'id']);
    if (!subscriptionId) {
      root.failure('Could not find a subscription. Please contact support @ info@automatedremarketing.com');
    }
    const planObj = { plan: get().selectedPlan[0] };
    try {
      await axios.post(`${API_URL}/api/payments/subscription/${subscriptionId}`, planObj);
      root.success('Your plan has been updated.');
    } catch (e) {
      root.failure('Could not find add Recover. Please contact support @ info@automatedremarketing.com');
    } finally {
      await root.accountStatus.getAccountDetails();
      set({ loading: false });
    }
  },
}));

export default usePaymentsStore;
